import { ActionFunctionArgs, json, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import HeaderBlock from "~/modules/pageBlocks/components/blocks/marketing/header/HeaderBlock";
import FooterBlock from "~/modules/pageBlocks/components/blocks/marketing/footer/FooterBlock";
import { MetaTagsDto } from "~/application/dtos/seo/MetaTagsDto";
import { getLinkTags } from "~/modules/pageBlocks/services/.server/pagesService";
import { getTranslations } from "~/locale/i18next.server";
import { DataSourceTypeDto, DataSourceTypes } from "~/modules/datalang/dtos/DataSourceType";
import { redirect, useTypedActionData, useTypedLoaderData } from "remix-typedjson";
import Page404 from "~/components/pages/Page404";
import BreadcrumbSimple from "~/components/ui/breadcrumbs/BreadcrumbSimple";
import { useTranslation } from "react-i18next";
import { Form, useNavigation, useSubmit } from "@remix-run/react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import UnderConstruction from "~/components/ui/misc/UnderConstruction";
import DataSourceConfigForm from "~/modules/datalang/components/DataSourceConfig";
import DataViewConfigForm from "~/modules/datalang/components/DataViewConfigForm";
import DataViewsServer from "~/modules/datalang/services/DataViews.server";
import AssistantDemoServer from "~/modules/datalang/services/AssistantDemo.server";
import { getAnalyticsInfo } from "~/utils/analyticsCookie.server";
import clsx from "clsx";
import NotificationService from "~/modules/notifications/services/.server/NotificationService";
import { MediaDto } from "~/application/dtos/entities/MediaDto";
import DataSourceUtils from "~/modules/datalang/utils/DataSourceUtils";
import DataSourceIcons from "~/modules/datalang/components/icons/DataSourceIcons";
import { getBaseURL } from "~/utils/url.server";
import ButtonPrimary from "~/components/ui/buttons/ButtonPrimary";
import ButtonSecondary from "~/components/ui/buttons/ButtonSecondary";
import InfoBanner from "~/components/ui/banners/InfoBanner";
import InputRadioGroupCards from "~/components/ui/input/InputRadioGroupCards";
import RedirectsService from "~/modules/redirects/RedirectsService";

export const meta: MetaFunction<typeof loader> = ({ data }) => data?.metatags || [];

type LoaderData = {
  metatags: MetaTagsDto;
  dataSourceType: DataSourceTypeDto;
  defaultValues?: {
    dataCredentialsConfig?: any;
    dataViewConfig?: any;
  };
  keys: {
    google?: { clientId: string; apiKey: string; appId: string };
    notion?: { clientId: string; clientSecret: string; authUrl: string; redirectUri: string };
  };
};
export let loader = async ({ params, request }: LoaderFunctionArgs) => {
  await RedirectsService.findAndRedirect({ request });
  const { t } = await getTranslations(request);
  const dataSourceType = DataSourceTypes.find((f) => f.demoSlug === params.dataSource);
  if (!dataSourceType) {
    return json({ metatags: [{ title: `Invalid Data Source | ${process.env.APP_NAME}` }, ...getLinkTags(request)] }, { status: 404 });
  }
  let defaultValues: any | undefined = undefined;
  if (process.env.NODE_ENV === "development") {
    if (dataSourceType.value === "database" && process.env.DEFAULT_DATABASE_CONNECTION_STRING) {
      defaultValues = {
        dataCredentialsConfig: {
          connectionString: process.env.DEFAULT_DATABASE_CONNECTION_STRING,
        },
        dataViewConfig: {
          sql: process.env.DEFAULT_DATABASE_SQL,
        },
      };
    } else if (dataSourceType.value === "server" && process.env.DEFAULT_SERVER_CONNECTION_STRING) {
      defaultValues = {
        dataCredentialsConfig: {
          connectionString: process.env.DEFAULT_SERVER_CONNECTION_STRING,
          url: process.env.DEFAULT_SERVER_URL,
          apiKey: process.env.DEFAULT_SERVER_API_KEY,
          connectionType: process.env.DEFAULT_SERVER_CONNECTION_TYPE,
        },
        dataViewConfig: {
          sql: process.env.DEFAULT_SERVER_SQL,
        },
      };
    } else if (dataSourceType.value === "snowflake" && process.env.DEFAULT_SNOWFLAKE_ACCOUNT) {
      defaultValues = {
        dataCredentialsConfig: {
          account: process.env.DEFAULT_SNOWFLAKE_ACCOUNT,
          username: process.env.DEFAULT_SNOWFLAKE_USERNAME,
          password: process.env.DEFAULT_SNOWFLAKE_PASSWORD,
          database: process.env.DEFAULT_SNOWFLAKE_DATABASE,
          schema: process.env.DEFAULT_SNOWFLAKE_SCHEMA,
          application: process.env.DEFAULT_SNOWFLAKE_APPLICATION || "DataLang Demo",
        },
        dataViewConfig: {
          sql: process.env.DEFAULT_SNOWFLAKE_SQL,
        },
      };
    } else if (dataSourceType.value === "postgres" && process.env.DEFAULT_POSTGRES_HOST) {
      defaultValues = {
        dataCredentialsConfig: {
          host: process.env.DEFAULT_POSTGRES_HOST,
          port: process.env.DEFAULT_POSTGRES_PORT,
          username: process.env.DEFAULT_POSTGRES_USERNAME,
          password: process.env.DEFAULT_POSTGRES_PASSWORD,
          database: process.env.DEFAULT_POSTGRES_DATABASE,
        },
        dataViewConfig: {
          sql: process.env.DEFAULT_POSTGRES_SQL,
        },
      };
    } else if (dataSourceType.value === "mysql" && process.env.DEFAULT_MYSQL_HOST) {
      defaultValues = {
        dataCredentialsConfig: {
          host: process.env.DEFAULT_MYSQL_HOST,
          port: process.env.DEFAULT_MYSQL_PORT,
          username: process.env.DEFAULT_MYSQL_USERNAME,
          password: process.env.DEFAULT_MYSQL_PASSWORD,
          database: process.env.DEFAULT_MYSQL_DATABASE,
        },
        dataViewConfig: {
          sql: process.env.DEFAULT_MYSQL_SQL,
        },
      };
    } else if (dataSourceType.value === "mssql" && process.env.DEFAULT_MSSQL_HOST) {
      defaultValues = {
        dataCredentialsConfig: {
          host: process.env.DEFAULT_MSSQL_HOST,
          port: process.env.DEFAULT_MSSQL_PORT,
          username: process.env.DEFAULT_MSSQL_USERNAME,
          password: process.env.DEFAULT_MSSQL_PASSWORD,
          database: process.env.DEFAULT_MSSQL_DATABASE,
        },
        dataViewConfig: {
          sql: process.env.DEFAULT_MSSQL_SQL,
        },
      };
    } else if (dataSourceType.value === "notion") {
      defaultValues = {
        dataCredentialsConfig: {},
        dataViewConfig: {
          accessToken: "secret_zqBt4ijGVfeoz0lmCHPVwl5aOd6gi2AlIoyd4q0W4TN",
          selectedItems: [{ id: "37e706fb-be07-41e0-ab19-6b78b5c5d142", object: "page", title: "DataLang" }],
          workspace_icon: "https://s3-us-west-2.amazonaws.com/public.notion-static.com/c563c876-74a3-47b4-900b-e1fea62ca4fa/icon.png",
          workspace_name: "DataLang",
        },
      };
    } else if (dataSourceType.value === "content") {
      defaultValues = {
        dataCredentialsConfig: {},
        dataViewConfig: {
          type: "text",
          content: "Welcome to DataLang!",
        },
      };
    } else if (dataSourceType.value === "hubspot") {
      defaultValues = {
        dataCredentialsConfig: {
          accessToken: process.env.DEFAULT_HUBSPOT_API_KEY,
        },
        dataViewConfig: {
          objectType: process.env.DEFAULT_HUBSPOT_OBJECT_TYPE,
        },
      };
    }
  }
  if (dataSourceType.value === "googleSheets") {
    defaultValues = {
      dataCredentialsConfig: {},
      dataViewConfig: {
        type: "url",
        url: "https://docs.google.com/spreadsheets/d/1H1ylUyZ1HZbvsPXZFOFNa3he47TQozM_MPof2CHvehY/edit",
      },
    };
  } else if (dataSourceType.value === "website") {
    defaultValues = {
      dataCredentialsConfig: {},
      dataViewConfig: {
        type: "pages",
        urls: [
          {
            format: "markdown",
            url: "https://saasrock.com",
            title: "Landing Page",
          },
          {
            format: "markdown",
            url: "https://saasrock.com/pricing",
            title: "Pricing",
          },
          {
            format: "markdown",
            url: "https://saasrock.com/docs/articles/introduction",
            title: "Introduction",
          },
        ],
      },
    };
  }
  const data: LoaderData = {
    metatags: [
      { title: `${t(`datalang.demos.${dataSourceType.value}.title`)} | ${t("shared.demo")} | ${process.env.APP_NAME}` },
      { description: t(`datalang.demos.${dataSourceType.value}.description`) },
      ...getLinkTags(request),
    ],
    dataSourceType,
    defaultValues,
    keys: {
      google: {
        clientId: process.env.GOOGLE_CLIENT_ID || "",
        apiKey: process.env.GOOGLE_API_KEY || "",
        appId: process.env.GOOGLE_PROJECT_ID || "",
      },
      notion: {
        clientId: process.env.NOTION_OAUTH_CLIENT_ID || "",
        clientSecret: process.env.NOTION_OAUTH_CLIENT_SECRET || "",
        authUrl: process.env.NOTION_OAUTH_AUTH_URL || "",
        redirectUri: `${getBaseURL(request)}/oauth/notion/callback`,
      },
    },
  };
  return json(data);
};

type ActionData = {
  success?: string;
  error?: string;
  resultData?: { error?: string; data?: any; sizeInMB?: number };
  message?: string;
};
export const action = async ({ request, params }: ActionFunctionArgs) => {
  const { userAnalyticsId } = await getAnalyticsInfo(request);
  const form = await request.formData();
  const action = form.get("action");
  const dataSourceType = DataSourceTypes.find((f) => f.demoSlug === params.dataSource);
  if (!dataSourceType) {
    return json({ error: "Invalid Data Source" }, { status: 400 });
  }
  if (action === "chat") {
    const dataCredentialsConfig = form.get("data-credential-config")?.toString() || "";
    const dataViewConfig = form.get("dataview-config")?.toString() || "";
    if (!dataCredentialsConfig) {
      return json({ error: "Data source config is required" }, { status: 400 });
    } else if (!dataViewConfig) {
      return json({ error: "Config is required" }, { status: 400 });
    }
    try {
      const demo = await AssistantDemoServer.run({
        userAnalyticsId,
        dataSourceType: dataSourceType.value,
        dataCredentialsConfig,
        dataViewConfig,
      });
      NotificationService.sendToRoles({
        channel: "admin-users",
        tenantId: null,
        notification: {
          message: `Created assistant demo [${dataSourceType.value}]: ${demo.id}`,
          action: { url: `/admin/demo` },
        },
      });
      return redirect(`/chat-with/${dataSourceType.demoSlug}/demo/${demo.id}`);
    } catch (e: any) {
      return json({ error: e.message }, { status: 400 });
    }
  } else if (action === "test") {
    try {
      const dataViewConfig = form.get("dataview-config")?.toString() || "";
      const dataCredentialsConfig = form.get("data-credential-config")?.toString() || "";
      const dataRun = await DataViewsServer.run({
        type: dataSourceType.value,
        dataGroup: {
          slug: "demo",
        },
        dataCredentials: {
          config: dataCredentialsConfig,
        },
        dataView: {
          config: dataViewConfig,
          slug: "demo",
        },
        decrypt: false,
        tenantId: null,
      });
      // NotificationService.sendToRoles({
      //   channel: "admin-users",
      //   tenantId: null,
      //   notification: {
      //     message: `Tested data source: ${dataSourceType.value}`,
      //   },
      // });
      return json(
        {
          success: "Test successful",
          resultData: {
            data: dataRun.data,
            sizeInMB: Buffer.byteLength(JSON.stringify(dataRun.data), "utf8") / 1000000,
          },
        },
        { status: 200 }
      );
    } catch (e: any) {
      return json({ resultData: { error: e.message } }, { status: 400 });
    }
  } else {
    return json({ error: "Invalid action" }, { status: 400 });
  }
};

export default function () {
  const { t } = useTranslation();
  const data = useTypedLoaderData<LoaderData>();
  const actionData = useTypedActionData<ActionData>();
  useEffect(() => {
    if (actionData?.success) {
      toast.success(actionData.success);
    } else if (actionData?.error) {
      toast.error(actionData.error);
    } else if (actionData?.resultData?.error) {
      toast.error(actionData.resultData.error);
    }
  }, [actionData]);
  return (
    <div>
      {!data?.dataSourceType ? (
        <Page404 />
      ) : (
        <div>
          <HeaderBlock />
          <div className="sm:align-center mx-auto mb-40 max-w-2xl space-y-4 overflow-hidden px-4 py-12 sm:flex sm:flex-col sm:px-6 sm:py-6 lg:px-8">
            <div>{/* <div className="mx-auto text-center text-base font-black uppercase">{t("shared.demo")}</div> */}</div>
            <div className="text-left">
              <div className="flex items-center space-x-2">
                <DataSourceIcons type={data.dataSourceType.value} className="h-8 w-8" />
                <h1 className="text-2xl font-extrabold tracking-tight"> {t(`datalang.demos.${data.dataSourceType.value}.title`)}</h1>
              </div>
              <h2 className="text-muted-foreground mt-2 text-lg leading-6">{t(`datalang.demos.${data.dataSourceType.value}.description`)}</h2>
            </div>
            <div className="space-y-2 overflow-y-auto overflow-x-hidden">
              <BreadcrumbSimple
                menu={[
                  {
                    title: "Demos",
                    routePath: "/chat-with",
                  },
                  {
                    title: data.dataSourceType.name,
                  },
                ]}
              />
              {data.dataSourceType.premium && (
                <InfoBanner title="Premium Data Source" text="This data source type is only available to premium users (Pro or higher plans)." />
              )}
              <DataSourceDemo dataSourceType={data.dataSourceType} resultData={actionData?.resultData} defaultValues={data.defaultValues} keys={data.keys} />
            </div>
          </div>
          <FooterBlock />
        </div>
      )}
    </div>
  );
}

function DataSourceDemo({
  dataSourceType,
  resultData,
  defaultValues,
  keys,
}: {
  dataSourceType: DataSourceTypeDto;
  resultData?: { error?: string; data?: any; sizeInMB?: number };
  defaultValues?: {
    dataCredentialsConfig?: any;
    dataViewConfig?: any;
  };
  keys: {
    google?: { clientId: string; apiKey: string; appId: string };
    notion?: { clientId: string; clientSecret: string; authUrl: string; redirectUri: string };
  };
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const submit = useSubmit();

  const [dataCredentialsConfig, setDataCredentialsConfig] = useState<any>(defaultValues?.dataCredentialsConfig || {});
  const [dataViewConfig, setDataViewConfig] = useState<any>(defaultValues?.dataViewConfig || {});

  function onTest() {
    const form = new FormData();
    form.set("action", "test");
    form.set("data-credential-config", JSON.stringify(dataCredentialsConfig));
    form.set("dataview-config", JSON.stringify(dataViewConfig));
    submit(form, {
      method: "post",
    });
  }
  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData();
    form.set("action", "chat");
    form.set("data-credential-config", JSON.stringify(dataCredentialsConfig));
    form.set("dataview-config", JSON.stringify(dataViewConfig));
    submit(form, {
      method: "post",
    });
  }
  const isLoading = navigation.state === "submitting" && navigation.formData?.get("action") === "chat";
  function canRun() {
    if (isLoading) {
      return false;
    }
    if (dataSourceType.value === "file") {
      const media = dataViewConfig?.media as MediaDto[];
      if (!media || media.length === 0) {
        return false;
      } else {
        return true;
      }
    } else if (dataSourceType.value === "content") {
      if (!dataViewConfig?.content) {
        return false;
      }
    } else if (dataSourceType.value === "notion") {
      if (!dataViewConfig.accessToken) {
        return false;
      }
    }
    if (!DataSourceUtils.canTest(dataSourceType.value)) {
      return true;
    }
    if (resultData?.data) {
      return true;
    }
    return false;
  }
  function canTest() {
    if (!DataSourceUtils.canTest(dataSourceType.value)) {
      return false;
    }
    if (dataSourceType.value === "notion") {
      if (!dataViewConfig.accessToken) {
        return false;
      }
    }
    return true;
  }
  return (
    <div>
      {dataSourceType.underConstruction ? (
        <div className="w-full">
          <UnderConstruction />
        </div>
      ) : (
        <Form onSubmit={onSubmit} className="space-y-4 p-1">
          <DataSourceConfigForm type={dataSourceType.value} value={dataCredentialsConfig} onChange={setDataCredentialsConfig} />
          {/* {dataSourceType.views.length > 1 && (
            <InputRadioGroupCards
              title="Type"
              required
              value={type}
              onChange={(value) => {
                setType(!value ? "sql" : (value as DataViewType));
              }}
              options={dataSourceType.views.map((i) => ({
                name: t("datalang.dataViewTypes." + i.value),
                value: i.value,
              }))}
              display="name"
              columns={4}
            />
          )} */}
          <DataViewConfigForm
            dataSourceType={dataSourceType.value}
            value={dataViewConfig}
            onChange={setDataViewConfig}
            onTest={onTest}
            isDemo={true}
            resultData={resultData}
            keys={keys}
          />
          {/* {JSON.stringify(dataViewConfig)} */}

          <div className="flex flex-col items-center justify-end space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
            {DataSourceUtils.canTest(dataSourceType.value) && (
              <div className="w-full sm:w-auto">
                <ButtonSecondary
                  isLoading={navigation.state === "submitting" && navigation.formData?.get("action") === "test"}
                  disabled={!canTest()}
                  className={clsx("w-full sm:w-auto")}
                  event={{ action: "click", category: "data-source-demos", label: dataSourceType.name, value: t("datalang.demos.cta.testAssistant") }}
                  onClick={onTest}
                >
                  {DataSourceUtils.canTest(dataSourceType.value) ? <span>{t("datalang.demos.cta.testAssistant")}</span> : <span>{t("shared.test")}</span>}
                </ButtonSecondary>
              </div>
            )}
            <div className="w-full sm:w-auto">
              <ButtonPrimary
                disabled={!canRun()}
                type="submit"
                className={clsx("w-full", isLoading && "base-spinner")}
                event={{ action: "click", category: "data-source-demos", label: dataSourceType.name, value: t("datalang.demos.cta.createAssistant") }}
              >
                {t("datalang.demos.cta.createAssistant")}
              </ButtonPrimary>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
}
